// 在chat.js中修改接口实现
import requests from '../utils/axios'
import { showToast } from 'vant'
import indexedDB from '../utils/indexedDB'

/**
 * 编辑消息
 * @param {string} userId - 用户ID
 * @param {string} record_id - 记录ID
 * @param {string} answer - 编辑后的消息内容
 * @returns {Promise} 编辑结果Promise
 */
export const editMessage = async (userId, record_id, answer) => {
  try {
    const wecomToken = localStorage.getItem('wecom_access_token')

    if (!userId) throw new Error('未找到用户ID')
    if (!record_id) throw new Error('未找到记录ID')
    if (!answer) throw new Error('编辑内容不能为空')
    // if (!wecomToken) {
    //   // axios拦截器会处理未登录情况，这里保留检查以提供更明确的错误信息
    //   throw new Error('未获取到认证信息，请重新登录')
    // }

    const requestBody = {
      external_user_id: userId,
      record_id: record_id,
      answer: answer,
    }

    // 使用requests实例发送请求
    const response = await requests.post('/api/v1/chat/message/edit', requestBody, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${wecomToken}`,
      },
    })

    return response.data
  } catch (error) {
    console.error('编辑消息失败:', error)
    throw error
  }
}

/**
 * 发送消息投票
 * @param {string} userId - 用户ID
 * @param {string} record_id - 记录ID
 * @param {number} vote - 投票类型(1-有用, 2-一般)
 * @param {string|null} advice - 建议内容
 * @returns {Promise} 投票结果Promise
 */
export const sendMessageVote = async (userId, record_id, vote, advice = null) => {
  try {
    const wecomToken = localStorage.getItem('wecom_access_token')

    if (!userId) throw new Error('未找到用户ID')
    if (!record_id) throw new Error('未找到记录ID')
    // if (!wecomToken) {
    //   // axios拦截器会处理未登录情况，这里保留检查以提供更明确的错误信息
    //   throw new Error('未获取到认证信息，请重新登录')
    // }

    const requestBody = {
      external_user_id: userId,
      record_id: record_id,
      vote: vote,
      advice: advice,
    }

    // 使用requests实例发送请求
    const response = await requests.post('/api/v1/chat/message/vote', requestBody, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${wecomToken}`,
      },
    })

    return response.data
  } catch (error) {
    console.error('发送投票失败:', error)
    throw error
  }
}

/**
 * 记录消息发送
 * @param {string} userId - 用户ID
 * @param {string} record_id - 记录ID
 * @returns {Promise} 记录结果Promise
 */
export const sendMessageRecord = async (userId, record_id) => {
  try {
    const wecomToken = localStorage.getItem('wecom_access_token')

    if (!userId) throw new Error('未找到用户ID')
    if (!record_id) throw new Error('未找到记录ID')

    const requestBody = {
      external_user_id: userId,
      record_id: record_id,
    }

    // 使用requests实例发送请求
    const response = await requests.post('/api/v1/chat/message/send', requestBody, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${wecomToken}`,
      },
    })

    return response.data
  } catch (error) {
    throw error
  }
}

/**
 * 处理Markdown内容，提取实际内容
 * @param {string} content - 包含可能包含Markdown包装的内容
 * @returns {string} 处理后的纯内容
 */
const processMarkdownContent = (content) => {
  if (typeof content !== 'string') {
    return String(content)
  }

  // 确保markdown格式正确性，移除可能的特殊字符问题
  let processedContent = content.replace(/\r/g, '')

  // 提取markdown内容，去除```markdown\n和可能的```结束标记
  if (processedContent.startsWith('```markdown\n')) {
    processedContent = processedContent.substring('```markdown\n'.length)
  }

  // 移除结尾的```标记（如果存在）
  if (processedContent.endsWith('```')) {
    processedContent = processedContent.substring(0, processedContent.length - 3)
  }

  return processedContent
}

/**
 * 调用AI对话接口（流式版本）
 * @param {string} query - 用户发送的消息内容
 * @param {string} [externalUserid] - 外部联系人ID
 * @param {function} onChunk - 每收到一块数据的回调函数
 * @param {string} [record_id] - 历史对话记录ID（可选）
 * @param {int} [style] -话术风格
 * @param {string} [uuid] -消息索引
 * @returns {Promise} 完整回复结果Promise
 */
export const sendChatMessageStream = async (
  query,
  externalUserid = null,
  onChunk,
  record_id = null,
  messageIndex = -1,
  style=1,
  uuid=null,
  default_style = localStorage.getItem('default_style')
) => {
  try {
    const userId = externalUserid || localStorage.getItem('wecom_external_userid')
    const wecomToken = localStorage.getItem('wecom_access_token')

    if (!userId) throw new Error('未找到外部联系人ID')
    // 只有在没有提供record_id时，才需要检查query是否为空
    if (!record_id && !query?.trim()) throw new Error('请输入有效的消息内容')

    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${wecomToken}`,
    }

    // 构建请求体，根据是否有record_id来决定包含哪些字段
    const requestBody = {
      external_user_id: userId,
    }

    // 如果提供了record_id，则添加到请求体中，此时不包含query
    if (record_id) {
      requestBody.record_id = record_id
    } else {
      // 没有record_id时，才添加query
      requestBody.query = query.trim()
    }
    // 添加style和uuid参数（如果提供）
    if (style) {
      requestBody.style = style
    }
    if (uuid) {
      requestBody.uuid = uuid
    }
    if (default_style) {
      requestBody.default_style = default_style
    }
    const response =  await fetch(requests.defaults.baseURL + '/api/v1/chat/message', {
      method: 'POST',
      headers,
      body: JSON.stringify(requestBody),
      // 确保连接不会被过早终止
      keepalive: true,
    })

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let fullContent = ''
    let buffer = '' // 缓存不完整的内容
    let recordId = null // 存储record_id

    // 优化的分块处理逻辑
    while (true) {
      const { done, value } = await reader.read()

      if (done) {
        // 处理最后剩余的缓存
        if (buffer.trim()) {
          processBufferData(buffer)
        }
        break
      }

      // 解码当前分块并拼接到缓存
      buffer += decoder.decode(value, { stream: true })

      // 立即处理完整的数据行
      processBufferData(buffer)
    }

    // 辅助函数：处理缓存数据
    function processBufferData(dataBuffer) {
      // 分割数据行
      const lines = dataBuffer.split('\n')
      // 保留最后一行作为新的缓存（可能不完整）
      buffer = lines.pop() || ''

      // 逐行处理完整的数据
      lines.forEach((line) => {
        const trimmedLine = line.trim()
        if (!trimmedLine) return

        try {
          // 检查并移除data前缀
          let jsonStr = trimmedLine
          if (jsonStr.startsWith('data: ')) {
            jsonStr = jsonStr.slice(5).trim()
          }

          // 解析JSON
          const chunkData = JSON.parse(jsonStr)

          // 保存record_id（如果存在）
          if (chunkData.record_id) {
            if (recordId !== chunkData.record_id) {
              recordId = chunkData.record_id
            }
          }
          // 确保是回复类型且有内容
          if (chunkData.event_type === 'reply' && chunkData.content !== undefined) {
            // 更新完整内容
            fullContent = chunkData.content
            // 立即调用回调更新UI，实现流式效果
            onChunk(fullContent)
          }
        } catch (e) {
          // 非阻塞错误处理
          console.warn('解析分块数据失败:', e.message, '原始数据:', trimmedLine)
        }
      })
    }

    // 在方法返回前，将聊天记录存储到IndexedDB中
    try {
      // 直接根据调用参数判断是否是重新生成回复的场景
      const isRefreshMode = record_id !== null && record_id !== undefined && record_id.trim() !== ''
      const userId = externalUserid || localStorage.getItem('wecom_external_userid')
      if (isRefreshMode) {
        // 这是重新生成回复，更新现有记录
        // 首先获取现有记录
        let existingRecord = null;
        const allRecords = await indexedDB.getUserChatRecords(userId);

        // 尝试通过record_id查找记录
        existingRecord = allRecords.find(record => record.record_id === record_id);

        // 如果找不到，尝试在style数组中查找对应的record_id
        if (!existingRecord) {
          existingRecord = allRecords.find(record =>
            record.style && record.style.some(item => item.record_id === record_id)
          );
        }

        // 如果找不到，尝试通过messageIndex查找
        if (!existingRecord && messageIndex >= 0) {
          // 按时间戳排序
          const sortedRecords = [...allRecords].sort((a, b) => a.timestamp - b.timestamp);
          if (messageIndex < sortedRecords.length) {
            existingRecord = sortedRecords[messageIndex];
          }
        }

        if (existingRecord) {
          const updatedData = {};

          // 根据 style 字段更新对应的 style 相关对象
          const styleObject = {
            style: style,
            content: processMarkdownContent(fullContent),
            record_id: recordId
          };
          // 确保 style 数组存在
          if (!existingRecord.style) {
            updatedData.style = [styleObject];
          } else {
            // 查找是否已存在相同 style 的对象
            const existingStyleIndex = existingRecord.style.findIndex(item => Number(item.style) === Number(style));
            if (existingStyleIndex >= 0) {
              // 如果存在，更新该对象
              updatedData.style = [...existingRecord.style];

              updatedData.style[existingStyleIndex] = styleObject;
            } else {
              // 如果不存在，添加新对象
              updatedData.style = [...existingRecord.style, styleObject];
            }
          }
          updatedData.record_id = recordId;
          // 关键修复：根据查找方式决定使用哪个record_id进行更新
          let updateRecordId = existingRecord.record_id;

          // 如果是通过style数组中的record_id找到的记录，使用传入的record_id参数进行更新
          if (!allRecords.some(record => record.record_id === record_id) &&
              existingRecord.style && existingRecord.style.some(item => item.record_id === record_id)) {
            updateRecordId = record_id;
          }
          // 执行更新操作
          // await indexedDB.updateChatRecordByRecordId(existingRecord.record_id, updatedData);
          await indexedDB.updateChatRecordByRecordId(updateRecordId, updatedData);
        }
      } else {
        // 首先尝试根据 uuid 查找现有记录
        let existingRecord = null;
        if (uuid) {
          const allRecords = await indexedDB.getUserChatRecords(userId);
          // 查找相同 uuid 的记录
          existingRecord = allRecords.find(record => record.uuid === uuid);
        }

        if (existingRecord) {
          // 如果存在相同 uuid 的记录，更新该记录
          const updatedData = {};

          // 确保 style 数组存在
          if (!existingRecord.style) {
            updatedData.style = [];
          }

          // 根据 style 字段更新对应的 style 相关对象
          const styleObject = {
            style: style,
            content: processMarkdownContent(fullContent),
            record_id: recordId
          };

          // 查找是否已存在相同 style 的对象
          const existingStyleIndex = existingRecord.style?.findIndex(item => item.style === style);

          if (existingStyleIndex !== undefined && existingStyleIndex >= 0) {
            // 如果存在，更新该对象
            if (!updatedData.style) {
              updatedData.style = [...existingRecord.style];
            }
            updatedData.style[existingStyleIndex] = styleObject;
          } else {
            // 如果不存在，添加新对象
            if (!updatedData.style) {
              updatedData.style = existingRecord.style ? [...existingRecord.style] : [];
            }
            updatedData.style.push(styleObject);
          }

          // 使用记录的 record_id 进行更新
          await indexedDB.updateChatRecordByRecordId(existingRecord.record_id, updatedData);
        } else {
          // 如果不存在相同 uuid 的记录，创建新记录
          const chatRecord = {
            userId: externalUserid || localStorage.getItem('wecom_external_userid'),
            query: query?.trim(),
            timestamp: Date.now(),
            uuid: uuid,
            record_id:recordId,
            style: [{
              style: style,
              content: processMarkdownContent(fullContent),
              record_id: recordId
            }]
          };

          await indexedDB.storeChatRecord(chatRecord);
        }
      }
    } catch (dbError) {
      console.error('存储聊天记录到IndexedDB失败:', dbError)
      // 不影响主流程，继续返回结果
    }

    return {
      content: fullContent,
      record_id: recordId,
    }
  } catch (error) {
    return Promise.reject(error)
  }
}

// 保留原有的非流式接口
export const sendChatMessage = async (query, externalUserid = null) => {
  // 原有实现保持不变
  try {
    const userId = externalUserid || localStorage.getItem('wecom_external_userid')
    if (!userId) {
      throw new Error('未找到外部联系人ID，请确保已成功获取企业微信用户信息')
    }
    if (!query || typeof query !== 'string' || query.trim() === '') {
      throw new Error('请输入有效的消息内容')
    }
    const response = await requests.post('/api/v1/chat/message', {
      external_user_id: userId,
      query: query.trim(),
    })
    if (!response.data) {
      throw new Error('AI回复数据格式错误')
    }
    return response.data
  } catch (error) {
    return Promise.reject(error)
  }
}

/**
 * 获取图片的media_id
 * @param {Array<string>} urls - 图片URL数组
 * @returns {Promise} 包含media_id的响应结果
 */
export const getImageMediaIds = async (urls) => {
  try {
    const wecomToken = localStorage.getItem('wecom_access_token')
    const userId =
      localStorage.getItem('wecom_external_userid') || localStorage.getItem('wecom_external_chatid')

    if (!userId) throw new Error('未找到用户ID')
    // if (!wecomToken) throw new Error('未获取到认证信息，请重新登录')
    if (!urls || !Array.isArray(urls) || urls.length === 0) {
      throw new Error('请提供有效的图片URL数组')
    }

    // 过滤无效的URL
    const validUrls = urls.filter(
      (url) =>
        typeof url === 'string' &&
        url.trim() !== '' &&
        (url.startsWith('http://') || url.startsWith('https://')),
    )

    if (validUrls.length === 0) {
      throw new Error('未找到有效的图片URL')
    }

    const requestBody = {
      urls: validUrls,
    }

    const response = await requests.post('/api/v1/chat/media_ids', requestBody, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${wecomToken}`,
      },
    })

    return response
  } catch (error) {
    console.error('获取图片media_id失败:', error)
    throw error
  }
}

/**
 * 设置局部默认风格
 * @param {string} userId - 用户ID
 * @param {string} uuid - 记录ID
 * @param {number|string} defaultStyle - 默认风格
 * @param {string|null} [recordId] - 当前消息 record_id，写入 payload.record_id 及顶层 record_id
 * @returns {Promise} 记录结果Promise
 */
export const sendDefalutStyle = async (userId, uuid, defaultStyle = 1, recordId) => {
  try {
    const wecomToken = localStorage.getItem('wecom_access_token')

    if (!userId) throw new Error('未找到用户ID')
    if (!uuid) throw new Error('未找到uuid')

    // 嵌套 payload 供后端约定；顶层同时带 target_style / record_id，避免网关或中间层丢弃嵌套字段时丢失
    const requestBody = {
      external_user_id: userId,
      uuid: uuid,
      default_style: defaultStyle,
      target_style: defaultStyle,
      record_id: recordId,
    }

    // 使用requests实例发送请求
    const response = await requests.post('/api/v1/chat/message/default_style', requestBody, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${wecomToken}`,
      },
    })

    return response.data
  } catch (error) {
    throw error
  }
}

/**
 * 查询默认风格
 * @param {string} userId - 用户ID
 * @param {string} uuid - 记录ID
 * @returns {Promise} 记录结果Promise
 */
export const getDefalutStyle = async () => {
  try {
    // 使用requests实例发送请求
    const response = await requests.get('/api/v1/chat/user/default_style')

    return response.data
  } catch (error) {
    throw error
  }
}

/**
 * 设置全局风格
 * @param {string} userId - 用户ID
 * @param {string} uuid - 记录ID
 * @returns {Promise} 记录结果Promise
 */
export const updateDefaultStyle = async (userId,defaultStyle=1) => {
  try {
    const wecomToken = localStorage.getItem('wecom_access_token')

    if (!userId) throw new Error('未找到用户ID')

    const requestBody = {
      external_user_id: userId,
      default_style: defaultStyle,
    }

    // 使用requests实例发送请求
    const response = await requests.post('/api/v1/chat/user/update/default_style', requestBody, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${wecomToken}`,
      },
    })

    return response.data
  } catch (error) {
    throw error
  }
}

export default {
  sendChatMessage,
  sendChatMessageStream,
  sendMessageVote,
  sendMessageRecord,
  getImageMediaIds,
  sendDefalutStyle,
  getDefalutStyle,
  updateDefaultStyle,
}
